import React, { useEffect, useRef } from 'react';

interface IvsVideoJsPlayerProps {
  /** Amazon IVS playback URL (HLS) */
  streamUrl: string;
  /** Optional styling */
  width?: string;
  height?: string;
  onError: () => Promise<void>;
}



const IvsVideoJsPlayer: React.FC<IvsVideoJsPlayerProps> = ({
  streamUrl,
  width = '100%',
  height = 'auto',
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);

  useEffect(() => {
    const init = async () => {
      // wait up to 5 seconds for videojs & IVS plugins to arrive
      for (let i = 0; i < 10; i++) {
        if (window.videojs && window.registerIVSTech && window.registerIVSQualityPlugin) {
          break;
        }
        await new Promise(r => setTimeout(r, 500));
      }

      if (!window.videojs) {
        console.error('Video.js never loaded');
        return;
      }
      if (!window.registerIVSTech) {
        console.error('IVS Tech plugin never loaded');
        return;
      }
      if (!window.registerIVSQualityPlugin) {
        console.error('IVS Quality plugin never loaded');
        return;
      }

      // Register IVS tech & quality plugin
      window.registerIVSTech(window.videojs);
      window.registerIVSQualityPlugin(window.videojs);

      // Initialize Video.js player
      const player = window.videojs(videoRef.current, {
        techOrder: ['AmazonIVS'],
        controls: true,
        autoplay: true,
        muted: false,
        fluid: true,
        controlBar: {
          fullscreenToggle: true,
          playToggle: { replay: false },
          pictureInPictureToggle: true,
        },
      });

      // Enable manual quality selection
      player.enableIVSQualityPlugin();

      // Load the IVS stream
      player.src(streamUrl);
      player.volume(0.5);

      // Error handling
      player.on('error', async (err: any) => {
        console.error('IVS player error', err);
        await onError();
      });

      playerRef.current = player;
    };

    init();

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [streamUrl, onError]);

  return (
    <div style={{ width, height }}>
      <video
        ref={videoRef}
        className="video-js vjs-big-play-centered"
        playsInline
      />
    </div>
  );
};

export default IvsVideoJsPlayer;
